import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { CustomerErrorCode } from '@/services/customer/customer.service';
import { getSignInPath, Routes } from '@/router';
import { ChipotleuReVerificationContentFragment } from '@/models/cms';

@Component({
  name: 'CmgChipotleUVerification',
})
export default class CmgChipotleUVerification extends Vue {
  idMeClientId = '';
  idMeRedirectUrl = '';
  chipotleuReVerification: ChipotleuReVerificationContentFragment = {};

  get chipotleUBackgroundUrl(): string {
    return this.$appStore.state.cms.images.loyalty.chipUVerificationBackground.default;
  }

  get chipotleULogo(): string {
    return this.$appStore.state.cms.images.loyalty.chipotleULogo.default;
  }

  get title(): string {
    return this.chipotleuReVerification.headline || 'rewards';
  }

  get preheading(): string {
    return this.chipotleuReVerification.subheadline || 'chipotle u';
  }

  get description(): string {
    return (
      this.chipotleuReVerification.body ||
      '<p>One last step! Head to ID.me to consent to sharing the school information you previously provided so we can begin or continue your membership.</p>'
    );
  }

  get ctaText(): string {
    return this.chipotleuReVerification.ctaText || 'continue to id.me';
  }

  get isCustomerStudent(): boolean {
    return this.$appStore.getters.isActiveStudent;
  }

  get rewardTermsUrl(): string {
    return `${window.location.origin}/rewards-terms`;
  }

  onVerifyClick(): void {
    const authUrl = new URL('https://api.id.me/oauth/authorize');
    authUrl.searchParams.set('client_id', this.idMeClientId);
    authUrl.searchParams.set('redirect_uri', this.idMeRedirectUrl);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('scope', 'student');
    authUrl.searchParams.set('display', 'popup');

    const width = 600;
    const height = 700;
    const left = Math.round((window.screen.width - width) / 2);
    const top = Math.round((window.screen.height - height) / 2);
    window.open(authUrl.toString(), 'idme-popup', `width=${width},height=${height},left=${left},top=${top}`);
  }

  async mounted(): Promise<void> {
    this.idMeClientId = this.$services.config.getIdMeClientId();
    this.idMeRedirectUrl = this.$services.config.getIdMeRedirectUrl();

    await this.$services.featureFlags.waitForSplitReady();

    if (!this.$appStore.state.featureFlags.featureFlags.isChipotleUReverificationEnabled) {
      window.location.replace(`${window.location.origin}/chipotle-u-verify`);
      return;
    }

    const isLoggedIn = await this.$services.customer.waitForCustomerInitialization();
    if (!isLoggedIn) {
      this.$router.push(getSignInPath(this.$route.path));
      return;
    }

    if (!this.isCustomerStudent) {
      this.$services.ui.setFirstTimeChipotleUVerification(true);
    }

    window.addEventListener('message', this.onIdMeCallback);

    await this.$services.config.setChipotleuReVerification();
    this.chipotleuReVerification = await this.$services.config.getChipotleuReVerification();
  }

  beforeDestroy(): void {
    window.removeEventListener('message', this.onIdMeCallback);
  }

  onIdMeCallback = (ev: Event): void => {
    this.listenForIdMeCallback(ev);
  };

  private async listenForIdMeCallback(ev: Event): Promise<void> {
    const event = ev as MessageEvent;

    if (
      event &&
      event.origin === new URL(this.idMeRedirectUrl).origin &&
      event.data &&
      JSON.parse(event.data).type === 'IdMe' &&
      JSON.parse(event.data).secCode === 'security'
    ) {
      try {
        const code = JSON.parse(event.data).code;
        if (code === null) {
          this.showWeHitASnagErrorModal();
        } else {
          this.$services.ui.setAppPepperVisibility(true);
          await this.$services.customer.updateStudentStatus(code, true).then(async () => {
            this.$services.ui.setReferredFromChipotleUVerificationPage(true);
            await this.$router.push({ name: Routes.RewardsExchange });
          });
          this.$services.ui.setAppPepperVisibility(false);
        }
      } catch (error: any) {
        this.$services.logger.error('Failed to verify student status', undefined, error);
        if (error.errorCode === CustomerErrorCode.StudentIdAlreadyTaken) {
          this.$services.ui.setModalVisibility('basicError', true, {
            header: 'THIS STUDENT ID IS ALREADY TAKEN',
            content: 'It looks like this student ID is already linked to an existing Chipotle account. Log in to the associated account to access Chipotle U Rewards.',
            options: [
              {
                text: 'OKAY',
                onClick: () => {
                  this.$services.ui.setModalVisibility('basicError', false);
                  this.$router.push(Routes.Rewards);
                },
              },
            ],
            isShort: true,
            onClose: () => this.$services.ui.setModalVisibility('basicError', false),
          });
        } else {
          this.showWeHitASnagErrorModal();
        }
      }
      finally {
        this.$services.ui.setAppPepperVisibility(false);
      }
    }
  }

  private showWeHitASnagErrorModal(): void {
    this.$services.ui.setModalVisibility('basicError', true, {
      header: 'WE HIT A SNAG',
      content: 'Sorry, something went wrong on our end. Please try again.',
      options: [
        {
          text: 'OKAY',
          onClick: () => {
            this.$services.ui.setModalVisibility('basicError', false);
          },
        },
      ],
      isShort: true,
      onClose: () => this.$services.ui.setModalVisibility('basicError', false),
    });
  }
}
